import { useState } from 'react';
import { MapContainer, TileLayer, ZoomControl } from 'react-leaflet';
import { PropertyMarkers } from './PropertyMarkers';
import { useAuth } from '../lib/auth';
import { BASEMAP_ATTRIBUTION, BASEMAP_SUBDOMAINS, BASEMAP_URL } from '../lib/basemap';
import { HeatmapLayer, scoreToColor } from './HeatmapLayer';
import { SocioLayer, gradoToColor } from './SocioLayer';
import { GRADOS_REZAGO } from '../lib/types';
import { FlyToOnFilters } from './FlyToOnFilters';
import { FlyToOnRequest, type FlyRequest } from './FlyToOnRequest';
import { isEmpty, type Filters } from '../lib/filters';
import { OPP_TIER_LABELS } from '../lib/opportunity';
import { MAP_MAX_BOUNDS } from '../lib/mexico';
import { openLogin } from '../lib/signup';

interface Props {
  showProperties: boolean;
  showHeatmap: boolean;
  showSocio: boolean;
  filters: Filters;
  selectedId: string | null;
  onSelectProperty: (id: string | null) => void;
  /** Total global de properties que matchean los filtros (de /v1/facets). */
  matchedTotal: number | null;
  /** Cuando el user selecciona un lugar del autocomplete, el padre setea esto. */
  flyRequest?: FlyRequest | null;
  /** Ajusta el encuadre del mapa al bounding box del set filtrado. */
  onCenterResults?: () => void;
  /** Vuelve a encuadrar todo el país. */
  onFitMexico?: () => void;
  /** Hover sync lista↔mapa: id bajo el cursor + notificador. */
  hoveredId?: string | null;
  onHover?: (id: string | null) => void;
}

// Centro inicial: aprox. centro geográfico de los 3 estados piloto
// (CDMX 09 + Jalisco 14 + Nuevo León 19). Zoom 5 muestra todo México.
const CENTER: [number, number] = [22.5, -100.5];
const INITIAL_ZOOM = 5;

export function MapView({ showProperties, showHeatmap, showSocio, filters, selectedId, onSelectProperty, matchedTotal, flyRequest, onCenterResults, onFitMexico, hoveredId, onHover }: Props) {
  const { user } = useAuth();
  // Sin sesión no hay inventario en el mapa (2026-10-08: se apagó la capa
  // pública por inmueble). Se ven las capas agregadas: vacancia y rezago.
  const showPins = showProperties && !!user;
  const [markerStatus, setMarkerStatus] = useState<{ loading: boolean; visible: number; total?: number | null }>({
    loading: false,
    visible: 0,
  });
  const filtersActive = !isEmpty(filters);

  // Estados posibles del pill, en orden de prioridad:
  //   1. cargando markers en esta vista
  //   2. el cap de 500 truncó la vista → leyenda "Mostrando 500 de N" (RFC-0021 A5)
  //   3. hay matches globales pero ninguno en el bbox actual → hint de zoom-out
  //   4. nada que mostrar (oculto)
  let statusNode: React.ReactNode = null;
  const mapTotal = markerStatus.total ?? matchedTotal;
  if (showPins && markerStatus.loading) {
    statusNode = (
      <div className="map-status">
        <span className="dot" />
        Cargando propiedades…
      </div>
    );
  } else if (
    showPins &&
    !markerStatus.loading &&
    markerStatus.visible >= 500 &&
    mapTotal !== null &&
    mapTotal > markerStatus.visible
  ) {
    // Truncamiento explícito: sin esto los usuarios inferían distribución
    // geográfica de una muestra del ~4% sin saberlo.
    statusNode = (
      <div
        className="map-status"
        title="El mapa muestra máximo 500 puntos por vista, priorizando el opportunity score. Acércate para ver el detalle de una zona."
      >
        Mostrando <strong>{markerStatus.visible.toLocaleString('es-MX')}</strong> de{' '}
        <strong>{mapTotal.toLocaleString('es-MX')}</strong> resultados (los de mayor score)
      </div>
    );
  } else if (
    showPins &&
    !markerStatus.loading &&
    markerStatus.visible === 0 &&
    matchedTotal !== null &&
    matchedTotal > 0
  ) {
    statusNode = (
      <div className="map-status is-warn">
        <span className="dot" />
        <strong>{matchedTotal.toLocaleString('es-MX')}</strong>
        {matchedTotal === 1 ? 'propiedad coincide' : 'propiedades coinciden'}, pero ninguna en esta vista
        {filtersActive ? ' · ajusta el zoom o quita filtros' : ' · acércate o desplaza el mapa'}
      </div>
    );
  }

  return (
    <>
      <MapContainer
        center={CENTER}
        zoom={INITIAL_ZOOM}
        scrollWheelZoom
        // Canvas en vez de SVG para los ~500 CircleMarkers: un solo
        // elemento canvas vs 500 nodos SVG re-pintados en cada pan/zoom.
        preferCanvas
        // El zoom va arriba a la DERECHA y debajo de la barra de filtros
        // (CSS: .leaflet-top sigue a --filterbar-h). Arriba a la izquierda
        // quedaba tapado por la barra y, con la lista abierta, por la lista.
        zoomControl={false}
        // Acotado a México: con un arrastre de más (o una coordenada mala)
        // el mapa terminaba en medio del océano, todo gris.
        minZoom={4}
        maxBounds={MAP_MAX_BOUNDS}
        maxBoundsViscosity={0.9}
        style={{ width: '100%', height: '100%' }}
      >
        <ZoomControl position="topright" zoomInTitle="Acercar" zoomOutTitle="Alejar" />
        <TileLayer
          attribution={BASEMAP_ATTRIBUTION}
          url={BASEMAP_URL}
          subdomains={BASEMAP_SUBDOMAINS}
          maxZoom={19}
        />
        {showHeatmap && <HeatmapLayer />}
        {showSocio && <SocioLayer />}
        <FlyToOnFilters filters={filters} />
        <FlyToOnRequest request={flyRequest ?? null} />
        {/* Sólo con sesión: puntos exactos y clickeables. */}
        {showPins && (
          <PropertyMarkers
            filters={filters}
            selectedId={selectedId}
            hoveredId={hoveredId}
            onSelect={onSelectProperty}
            onHover={onHover}
            onStatusChange={setMarkerStatus}
          />
        )}
      </MapContainer>
      {/* Fuera del MapContainer: ahí dentro, Leaflet toma cada clic y
          arrastre como gesto del mapa (el botón de plegar lo movía). */}
      {(showHeatmap || showPins || showSocio) && (
        <Legend showHeatmap={showHeatmap} showProperties={showPins} showSocio={showSocio} />
      )}
      {statusNode}
      {!user && (
        <div className="map-public-note">
          El inventario se ve con una cuenta ·{' '}
          <button type="button" className="map-public-cta" onClick={openLogin}>Iniciar sesión</button>
        </div>
      )}
      <div className="map-bottom-left">
        {showPins && onCenterResults && (
          <button
            type="button"
            className="map-center-btn"
            onClick={onCenterResults}
            title="Ajusta el encuadre del mapa a las propiedades que coinciden con los filtros actuales"
          >
            ⌖ <span className="lbl-long">Centrar en resultados</span><span className="lbl-short">Centrar</span>
          </button>
        )}
        {onFitMexico && (
          <button
            type="button"
            className="map-center-btn"
            onClick={onFitMexico}
            title="Vuelve a ver todo el país"
          >
            <span className="lbl-long">Ver todo México</span><span className="lbl-short">Todo México</span>
          </button>
        )}
      </div>
    </>
  );
}

/**
 * Leyenda dual: la capa de markers se colorea por opportunity score (tiers),
 * la capa heatmap por vacancy score (gradiente AGEB). Mostramos solo las
 * secciones de las capas activas para no saturar.
 */
function Legend({ showHeatmap, showProperties, showSocio }: { showHeatmap: boolean; showProperties: boolean; showSocio: boolean }) {
  const stops = [0, 25, 50, 75, 100];
  // Plegable: en el celular la leyenda abierta tapaba medio mapa, así que
  // ahí arranca cerrada.
  const [open, setOpen] = useState(() => typeof window === 'undefined' || window.innerWidth > 768);
  if (!open) {
    return (
      <div className="legend">
        <button type="button" className="legend-toggle" aria-expanded={false} onClick={() => setOpen(true)}>
          Leyenda <span aria-hidden>▴</span>
        </button>
      </div>
    );
  }
  return (
    <div className="legend is-open">
      <button type="button" className="legend-toggle" aria-expanded onClick={() => setOpen(false)}>
        Leyenda <span aria-hidden>▾</span>
      </button>
      {showProperties && (
        <div className="legend-section">
          <div className="legend-title">Opportunity Score (pines)</div>
          <div className="legend-tiers">
            {OPP_TIER_LABELS.map((t) => (
              <div key={t.tier} className="legend-tier">
                <span className="legend-dot" style={{ background: t.color }} />
                <span>{t.label}</span>
              </div>
            ))}
            <div className="legend-tier">
              <span className="legend-dot legend-dot-risk" />
              <span>Borde naranja = zona de riesgo</span>
            </div>
          </div>
        </div>
      )}
      {showHeatmap && (
        <div className="legend-section">
          <div className="legend-title">Vacancia (AGEB)</div>
          <div className="scale">
            {stops.map((s) => (
              <div key={s} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <div className="swatch" style={{ background: scoreToColor(s) }} />
                <span>{s}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 4, color: 'var(--text-dim)', fontSize: 10 }}>
            zoom ≥ 11 para ver polígonos
          </div>
        </div>
      )}
      {showSocio && (
        <div className="legend-section">
          <div className="legend-title">Rezago social (AGEB)</div>
          <div className="scale">
            {GRADOS_REZAGO.map((g) => (
              <div key={g} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <div className="swatch" style={{ background: gradoToColor(g) }} />
                <span style={{ fontSize: 9 }}>{g}</span>
              </div>
            ))}
          </div>
          {/* La escala se lee al revés que "nivel socioeconómico": rezago
              muy bajo es la MEJOR condición. Sin esta línea el usuario
              interpreta el verde como zona pobre. */}
          <div style={{ marginTop: 4, color: 'var(--text-dim)', fontSize: 10 }}>
            verde = menor rezago (mejor nivel) · zoom ≥ 10
          </div>
        </div>
      )}
    </div>
  );
}
